// frontend/src/hooks/useOptimizedSocket.js
import { useEffect, useCallback, useRef, useState } from 'react';
import { useAppActions, useAppSelectors } from '../contexts/AppContext';
import { useSocketInstance } from './useSocketInstance';

// helper function
const emitWithTimeout = (socket, event, data, timeout = 10000) => {
  return new Promise((resolve, reject) => {
    if (!socket.connected) {
        return reject(new Error('Нет подключения к серверу'));
    }
    
    const timer = setTimeout(() => reject(new Error(`Таймаут для события ${event}`)), timeout);
    
    socket.emit(event, data, (response) => {
      clearTimeout(timer);
      console.log(`${event} response:`, response);
      if (response?.status === 'ok') {
        resolve(response);
      } else {
        reject(new Error(response?.message || `Ошибка при событии ${event}`));
      }
    });
  });
}

/**
 * Хук инкапсулирует единственный экземпляр Socket.IO, регистрирует слушатели ОДИН раз
 * и предоставляет методы для работы с комнатами и чатом.
 */
export const useOptimizedSocket = () => {
    // Используем единственный экземпляр socket
    const socket = useSocketInstance();
    
    const { 
        setError, 
        clearError, 
        setRoomState, 
        addMessage, 
        removePendingMessage, 
        setIsHost,
        setIsSending,
        updateMessageStatus
    } = useAppActions();
    
    const { roomId } = useAppSelectors();
    
    const [isConnected, setIsConnected] = useState(false);
    const [connectionAttempts, setConnectionAttempts] = useState(0);
    const reconnectTimerRef = useRef(null);
    const maxReconnectAttempts = 5;

    // Безопасная обертка для emit, которую мы сделаем внутренней
    const emit = useCallback((event, ...args) => {
        if (!isConnected) {
            console.warn(`Попытка отправить событие "${event}" без соединения`);
            return;
        }
        
        try {
            socket.emit(event, ...args);
        } catch (error) {
            console.error(`Ошибка при отправке события "${event}":`, error);
        }
    }, [isConnected, socket]);
    
    /* -------------------- Обработчики событий -------------------- */
    const handleConnect = useCallback(() => {
        console.log('Socket подключен');
        setIsConnected(true);
        setConnectionAttempts(0);
        clearError();
        
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    }, [clearError]);

    const handleDisconnect = useCallback((reason) => {
        console.log('Socket отключен:', reason);
        setIsConnected(false);
        
        if (reason === 'io server disconnect') {
            // Отключение по инициативе сервера - переподключаемся автоматически
            socket.connect();
        } else if (reason === 'transport close' || reason === 'ping timeout') {
            // Проблемы с сетью - пытаемся переподключиться
            attemptReconnect();
        }
    }, []);

    const handleConnectError = useCallback((error) => {
        console.error('Ошибка подключения Socket:', error);
        setError('Ошибка подключения к серверу');
        attemptReconnect();
    }, [setError]);

    const handleRoomStateUpdate = useCallback((newState) => {
        console.log("🔄 [ROOM-STATE] Получено обновление состояния комнаты:", {
            roomId: newState?.id,
            users: newState?.users?.length,
            videoQueue: newState?.videoQueue?.length,
            currentVideoIndex: newState?.currentVideoIndex,
            fullQueue: newState?.videoQueue,
            rawState: newState
        });
        
        if (newState && typeof newState === 'object') {
            // Перевіряємо, чи є черга відео
            if (newState.videoQueue && Array.isArray(newState.videoQueue)) {
                console.log("📹 [VIDEO-QUEUE] Queue details:", {
                    queueLength: newState.videoQueue.length,
                    currentIndex: newState.currentVideoIndex,
                    videos: newState.videoQueue.map((video, index) => ({
                        index,
                        id: video.id,
                        title: video.title?.substring(0, 50) + '...',
                        platform: video.platform
                    }))
                });
            }
            
            setRoomState(newState);
            clearError();
        } else {
            console.error('❌ [ROOM-STATE] Получено невалидное состояние комнаты:', newState);
            setError('Ошибка синхронизации с сервером');
        }
    }, [setRoomState, setError, clearError]);

    const handleNewMessage = useCallback((message) => {
        const timestamp = new Date().toISOString();
        
        console.log(`💬 [CHAT-RECV] ${timestamp} Получено новое сообщение:`, {
            messageId: message.id,
            senderId: message.senderId,
            username: message.username,
            text: message.text?.substring(0, 50) + (message.text?.length > 50 ? '...' : ''),
            isOwnMessage: message.senderId === socket.id
        });
        
        // Добавляем сообщение в чат для всех сообщений
        addMessage(message);
        
        console.log(`✅ [CHAT-RECV] ${timestamp} Сообщение добавлено в основной список`);
    }, [addMessage, socket]);

    const handleBecameHost = useCallback(() => {
        console.log('Пользователь стал хостом');
        setIsHost(true);
    }, [setIsHost]);

    const handleMessageError = useCallback((errorData) => {
        const timestamp = new Date().toISOString();
        
        console.error(`❌ [CHAT-ERROR] ${timestamp} Получена ошибка от сервера:`, {
            error: errorData.error,
            code: errorData.code,
            tempId: errorData.tempId,
            details: errorData.details
        });
        
        // Удаляем pending сообщение при ошибке
        if (errorData.tempId) {
            removePendingMessage(errorData.tempId);
        }
        
        // Очищаем состояние отправки
        setIsSending(false);
        
        // Показываем ошибку пользователю
        setError(errorData.error || 'Ошибка отправки сообщения');
        
        console.log(`❌ [CHAT-ERROR] ${timestamp} Ошибка обработана, состояние очищено`);
    }, [removePendingMessage, setIsSending, setError]);

    const handleMessageDelivered = useCallback((data) => {
        const timestamp = new Date().toISOString();
        console.log(`✅ [CHAT-DELIVERY] ${timestamp} Получено подтверждение доставки сообщения:`, data);

        if (!data) return;

        const keyToRemove = data.tempId || data.messageId;
        if (keyToRemove) {
            removePendingMessage(keyToRemove);
        }

        if (data.messageId && data.status) {
            updateMessageStatus(data.messageId, data.status);
        }

        setIsSending(false);
    }, [removePendingMessage, updateMessageStatus, setIsSending]);

    const handleMessageStatusUpdate = useCallback((data) => {
        const { messageId, status, tempId } = data || {};
        console.log(`🔄 [CHAT-STATUS] Cтатус сообщения обновлен:`, { messageId, status, tempId });

        if (tempId) {
            removePendingMessage(tempId);
        }

        if (!tempId && (status === 'sent' || status === 'delivered')) {
            removePendingMessage(messageId);
        }

        if (messageId && status) {
            updateMessageStatus(messageId, status);
        }

        if (status === 'sent' || status === 'delivered') {
            setIsSending(false);
        }
    }, [removePendingMessage, updateMessageStatus, setIsSending]);

    const handleMessagesRead = useCallback((data) => {
        const { messageIds } = data || {};
        if (!Array.isArray(messageIds) || messageIds.length === 0) return;

        messageIds.forEach((messageId) => {
            updateMessageStatus(messageId, 'read');
        });
    }, [updateMessageStatus]);

    /* -------------------- Экспоненциальный переподключение -------------------- */
    const attemptReconnect = useCallback(() => {
        if (connectionAttempts >= maxReconnectAttempts) {
            setError('Не удалось восстановить соединение с сервером. Обновите страницу.');
            return;
        }
        
        setConnectionAttempts(prev => prev + 1);
        const delay = Math.min(1000 * Math.pow(2, connectionAttempts), 10000); // Экспоненциальная задержка
        
        console.log(`Попытка переподключения ${connectionAttempts + 1}/${maxReconnectAttempts} через ${delay}ms`);
        setError(`Переподключение... (попытка ${connectionAttempts + 1}/${maxReconnectAttempts})`);
        
        reconnectTimerRef.current = setTimeout(() => {
            socket.connect();
        }, delay);
    }, [connectionAttempts, maxReconnectAttempts, setError, socket]);

    const markMessagesAsRead = useCallback((messageIds) => {
        if (!roomId || !Array.isArray(messageIds) || messageIds.length === 0) return;
        emit('mark_messages_as_read', { roomId, messageIds });
    }, [roomId, emit]);

    /* -------------------- Основной useEffect -------------------- */
        

    useEffect(() => {
        console.log('🔧 [SOCKET-INIT] Инициализация обработчиков Socket.IO...');
        
        // Подключение к Socket.IO только если не подключен
        if (!socket.connected) {
            socket.connect();
        } else {
            setIsConnected(true);
        }
        
        // Перевіряємо, чи вже зареєстровані обробники подій
        const listeners = socket.listeners('message_delivered');
        if (listeners.length === 0) {
            console.log('📝 [SOCKET-INIT] Регистрируем новые обработчики событий...');
            
            socket.on('connect', handleConnect);
            socket.on('disconnect', handleDisconnect);
            socket.on('connect_error', handleConnectError);
            socket.on('room_state_updated', handleRoomStateUpdate);
            socket.on('new_message', handleNewMessage);
            socket.on('became_host', handleBecameHost);
            socket.on('message_delivered', handleMessageDelivered);
            socket.on('message_error', handleMessageError);
            socket.on('message_status_updated', handleMessageStatusUpdate);
            socket.on('messages_read', handleMessagesRead);
            
            console.log('✅ [SOCKET-INIT] Глобальные обработчики Socket.IO зарегистрированы.');
        } else {
            console.log(`⚠️ [SOCKET-INIT] Обработчики уже зарегистрированы (${listeners.length} для message_delivered), пропускаем регистрацию.`);
        }
        
        return () => {
            console.log('🧹 [SOCKET-CLEANUP] Очистка обработчиков Socket.IO...');
            
            // Очистка обработчиков при размонтировании компонента
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('connect_error', handleConnectError);
            socket.off('room_state_updated', handleRoomStateUpdate);
            socket.off('new_message', handleNewMessage);
            socket.off('became_host', handleBecameHost);
            socket.off('message_delivered', handleMessageDelivered);
            socket.off('message_error', handleMessageError);
            socket.off('message_status_updated', handleMessageStatusUpdate);
            socket.off('messages_read', handleMessagesRead);
            
            // Очистка таймера переподключения
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            
            console.log('✅ [SOCKET-CLEANUP] Обработчики Socket.IO очищены');
        };
    }, [socket, handleConnect, handleDisconnect, handleConnectError, handleRoomStateUpdate, handleNewMessage, handleBecameHost, handleMessageDelivered, handleMessageError, handleMessageStatusUpdate, handleMessagesRead]);

    /* -------------------- API методы -------------------- */
    
    // Принудительное переподключение
    const forceReconnect = useCallback(() => {
        setConnectionAttempts(0);
        socket.disconnect();
        setTimeout(() => socket.connect(), 1000);
    }, [socket]);
    
    // Регистрация пользователя
    const registerUser = useCallback((username) => {
        console.log('Отправляю register_user с username:', username);
        clearError();
        return emitWithTimeout(socket, 'register_user', { username });
    }, [socket, clearError]);
    
    // Создание комнаты
    const createRoom = useCallback(() => {
        return emitWithTimeout(socket, 'create_room', {});
    }, [socket]);
    
    // Присоединение к комнате
    const joinRoom = useCallback((roomId) => {
        if (!roomId?.trim()) {
            return Promise.reject(new Error('Введите код комнаты'));
        }
        return emitWithTimeout(socket, 'join_room', { roomId: roomId.trim() });
    }, [socket]);

    /* -------------------- Отправка сообщений -------------------- */
    const sendMessage = useCallback((message, tempId) => {
        return new Promise((resolve, reject) => {
            const timestamp = new Date().toISOString();
            const generatedTempId = tempId || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            console.log(`📤 [CHAT-SEND] ${timestamp} Инициирована отправка сообщения:`, {
                tempId: generatedTempId,
                roomId,
                messageLength: message.length,
                messagePreview: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
                socketConnected: socket.connected,
                socketId: socket.id,
                isConnected,
                validationChecks: {
                    hasConnection: isConnected,
                    hasRoomId: !!roomId,
                    hasMessage: !!message.trim(),
                    socketReady: socket.connected
                }
            });
            
            // Валидация перед отправкой
            if (!isConnected) {
                console.error(`❌ [CHAT-SEND] ${timestamp} Отправка отклонена - нет подключения к серверу`);
                reject(new Error('Нет подключения к серверу'));
                return;
            }
            
            if (!roomId) {
                console.error(`❌ [CHAT-SEND] ${timestamp} Отправка отклонена - не находитесь в комнате`);
                reject(new Error('Не находитесь в комнате'));
                return;
            }
            
            if (!message.trim()) {
                console.error(`❌ [CHAT-SEND] ${timestamp} Отправка отклонена - пустое сообщение`);
                reject(new Error('Сообщение не может быть пустым'));
                return;
            }
            
            try {
                console.log(`📤 [CHAT-SEND] ${timestamp} Отправляем сообщение через Socket.IO:`, {
                    tempId: generatedTempId,
                    roomId,
                    trimmedMessage: message.trim()
                });
                
                // Отправляем в новом формате с tempId
                socket.emit('send_message', {
                    roomId,
                    messageText: message.trim(),
                    tempId: generatedTempId
                });
                
                console.log(`✅ [CHAT-SEND] ${timestamp} Сообщение отправлено успешно, ожидаем подтверждения доставки`);
                resolve(generatedTempId); // Возвращаем tempId для tracking'а
                
            } catch (error) {
                const errorTimestamp = new Date().toISOString();
                console.error(`❌ [CHAT-SEND] ${errorTimestamp} Ошибка при отправке сообщения:`, {
                    tempId: generatedTempId,
                    error: error.message,
                    stack: error.stack,
                    roomId,
                    socketConnected: socket.connected
                });
                reject(error);
            }
        });
    }, [isConnected, roomId, socket]);

    // Покинуть комнату
    const leaveRoom = useCallback(() => {
        const timestamp = new Date().toISOString();
        
        console.log(`🚪 [SOCKET-LEAVE] ${timestamp} Инициирован выход из комнаты через socket:`, {
            roomId,
            isConnected,
            socketConnected: socket.connected,
            socketId: socket.id,
            canLeave: roomId && isConnected
        });
        
        if (roomId && isConnected) {
            try {
                console.log(`📤 [SOCKET-LEAVE] ${timestamp} Отправляем команду leave_room на сервер...`);
                socket.emit('leave_room', roomId);
                
                console.log(`✅ [SOCKET-LEAVE] ${timestamp} Команда leave_room отправлена успешно`);
                
            } catch (error) {
                const errorTimestamp = new Date().toISOString();
                console.error(`❌ [SOCKET-LEAVE] ${errorTimestamp} Ошибка при отправке команды выхода:`, {
                    error: error.message,
                    stack: error.stack,
                    roomId,
                    socketConnected: socket.connected
                });
            }
        } else {
            console.warn(`⚠️ [SOCKET-LEAVE] ${timestamp} Выход из комнаты невозможен:`, {
                roomId,
                isConnected,
                socketConnected: socket.connected,
                reason: !roomId ? 'no_room_id' : 'not_connected'
            });
        }
    }, [roomId, isConnected, socket]);
    
    

    /* -------------------- Video/Queue Management -------------------- */

    const addVideoByUrl = useCallback((videoUrl) => {
        if (!roomId || !videoUrl.trim()) return;
        emit('host_add_liked_videos_mock', { videoUrls: [videoUrl.trim()] });
    }, [roomId, emit]);

    const addMyLikedVideos = useCallback(() => {
        if (!roomId) return;
        // Моковые URL, как в AppContent.js
        const mockUrls = [
            'https://www.tiktok.com/@charlidamelio/video/7123456789012345678',
            'https://www.tiktok.com/@addisonre/video/7123456789012345679',
            'https://www.tiktok.com/@zachking/video/7123456789012345680'
        ];
        emit('host_add_liked_videos_mock', { videoUrls: mockUrls });
    }, [roomId, emit]);

    const removeVideoFromQueue = useCallback((arg) => {
        if (!roomId) return;
        if (typeof arg === 'object' && arg && (arg.videoId || typeof arg.videoIndex === 'number')) {
            emit('remove_video_from_queue', { videoId: arg.videoId, videoIndex: arg.videoIndex });
        } else {
            emit('remove_video_from_queue', { videoIndex: arg });
        }
    }, [roomId, emit]);

    const clearVideoQueue = useCallback(() => {
        if (!roomId) return;
        emit('clear_video_queue');
    }, [roomId, emit]);

    const playNextVideo = useCallback(() => {
        if (!roomId) return;
        emit('play_next_video', roomId);
    }, [roomId, emit]);

    const playPrevVideo = useCallback(() => {
        if (!roomId) return;
        emit('play_prev_video', roomId);
    }, [roomId, emit]);


    /* -------------------- API хука -------------------- */
    return {
        // Состояние
        isConnected,
        connectionAttempts,
        socket, // <-- Временно оставим для WebRTC, но в идеале его тоже надо скрыть
        
        // Методы для комнат и пользователей
        registerUser,
        createRoom,
        joinRoom,
        sendMessage,
        leaveRoom,
        forceReconnect,
        markMessagesAsRead,

        // Методы для управления видео
        addVideoByUrl,
        addMyLikedVideos,
        removeVideoFromQueue,
        clearVideoQueue,
        playNextVideo,
        playPrevVideo,
        
        // Утилиты для подписки/отписки (если нужны где-то еще)
        on: socket.on.bind(socket),
        off: socket.off.bind(socket)
    };
};

export default useOptimizedSocket;